import React from 'react';
import {
  HeaderSelectionRender,
  SelectionRender,
  MoreActionsRender,
} from '../Renders';
import {useResponsiveColumns} from './useResponsiveColumns';
import lodash from 'lodash';
import {useCallbackRef} from '@unthinkable/react-utils';

const resolveColumnsRecursively = (columns, resolvedColumns = []) => {
  columns.forEach(column => {
    if (!column) {
      return;
    }
    if (column?.children?.length > 0) {
      resolveColumnsRecursively(column.children, resolvedColumns);
    } else {
      resolvedColumns.push(column);
    }
  });
  return resolvedColumns;
};

const filterVisibleColumns = (columns, props) => {
  const filteredColumns = [];
  columns?.forEach(column => {
    if (!column) {
      return;
    }
    let {visible = true} = column;
    if (typeof visible === 'function') {
      visible = visible(props);
    }
    if (!visible) {
      return;
    }
    if (column.children?.length > 0) {
      const visibleChildren = filterVisibleColumns(column.children, props);
      if (visibleChildren.length > 0) {
        filteredColumns.push({...column, children: visibleChildren});
      }
    } else {
      filteredColumns.push(column);
    }
  });
  return filteredColumns;
};

export const useColumns = ({
  columns,
  moreActions,
  selection,
  offsetWidth,
  disableHeaderSelection,
  ...props
}) => {
  columns = lodash.cloneDeep(columns);

  columns = filterVisibleColumns(columns, props);
  columns = useResponsiveColumns(columns);

  if (!columns?.length) {
    return {
      columns: [],
      headerColumns: [],
    };
  }

  const moreActionsCallback = useCallbackRef(props => {
    return <MoreActionsRender {...props} actions={moreActions} />;
  });

  const selectionCallback = useCallbackRef(props => {
    return <SelectionRender {...props} selection={selection} />;
  });

  const headerSelectionCallback = useCallbackRef(props => {
    return (
      <HeaderSelectionRender
        {...props}
        selection={selection}
        disableHeaderSelection={disableHeaderSelection}
      />
    );
  });

  if (selection) {
    columns.unshift({
      width: 50,
      render: selectionCallback,
      header: {
        render: headerSelectionCallback,
      },
    });
  }
  if (moreActions) {
    columns.push({
      width: 45,
      render: moreActionsCallback,
    });
  }

  const headerColumns = columns;
  columns = resolveColumnsRecursively(columns, []);

  const columnsWidth = columns.reduce((acc, col) => {
    const {width, minWidth = 100} = col;
    const colWidth = width || minWidth;
    return acc + colWidth;
  }, 0);

  const diff = offsetWidth ? offsetWidth - columnsWidth : 0;

  const rowExceedsTable = diff < 0;
  if (rowExceedsTable) {
    columns.forEach(col => {
      const {width, minWidth = 100} = col;
      if (!width) {
        col.width = minWidth;
      }
    });
  } else if (diff > 0 && moreActions) {
    const hasAllColumnWidth = columns.every(col => col.width);
    if (hasAllColumnWidth) {
      const flexColumn = {flex: 1};
      columns.splice(columns.length - 1, 0, flexColumn);
      headerColumns.splice(headerColumns.length - 1, 0, flexColumn);
    }
  }

  return {
    columns,
    headerColumns,
    rowWidth: rowExceedsTable ? columnsWidth : undefined,
  };
};
